import React, { useState, useEffect, useMemo } from "react";

// Soft UI Dashboard React components
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";

// Custom styles for the Modal
import { toast } from "react-toastify";
import Grid from "@mui/material/Grid";
import { EditDietRecall, AddDietRecall } from "services/clientdetails";
import RecallChart from "./chartBox";
import Createable from "containers/Select";
import SuiInput from "components/SuiInput";
import { submitDataHandler } from "../utils";
import CustomButton from "containers/CustomButton";
import { v4 as uuidv4 } from "uuid";
import { updateRow } from "utils/common";
import { frequencyOfEatingOptions } from "constant";
import { dietTypeOptions } from "constant";
import { daySegments } from "constant";

function RecallModal({
  open,
  setSelectedClient,
  handleCloseModal,
  id,
  selectedItem,
  setSelectedItem,
  persona,
  setMainData,
  mode,
}) {
  //default modal fields value
  const defaultHandler = useMemo(() => {
    return {};
  }, []);

  const [formdata, setFormdata] = useState(defaultHandler);
  const [loader, setLoader] = useState(false);

  const defaultErrorv1 = {
    time: false,
    clientAte: false,
  };
  const [chartError, setChartError] = useState({
    earlyMorning: defaultErrorv1,
    breakfast: defaultErrorv1,
    midMorning: defaultErrorv1,
    brunch: defaultErrorv1,
    midMeal: defaultErrorv1,
    lunch: defaultErrorv1,
    postLunch: defaultErrorv1,
    evening: defaultErrorv1,
    beforeDinner: defaultErrorv1,
    dinner: defaultErrorv1,
    postDinner: defaultErrorv1,
    lateNight: defaultErrorv1,
  });

  //for submitting data
  const submitHandler = async (e) => {
    let keys = Object.keys(formdata);

    if (!formdata.waterIntake) {
      toast.error(`Please fill Water Intake field`);

      setErrorMessage({
        ...errorMessage,
        waterIntake: true,
      });
      return;
    }

    for (let i = 0; i < keys.length; i++) {
      if (
        formdata[keys[i]] &&
        keys[i] !== "dietRecallId" &&
        keys[i] !== "createdAt"
      ) {
        let recipe = formdata[keys[i]];
        let recipeId = keys[i];

        if (recipe.time || recipe.clientAte) {
          if (!recipe.time) {
            toast.error(`Please enter a Time for ${recipeId}`);
            setChartError({
              ...chartError,
              [recipeId]: {
                time: true,
              },
            });
            return;
          }

          if (!recipe.clientAte) {
            toast.error(`Please enter a What client ate field for ${recipeId}`);
            setChartError({
              ...chartError,
              [recipeId]: {
                clientAte: true,
              },
            });
            return;
          }
        }
      }
    }

    if (persona === "admin") {
      await submitDataHandler({
        setLoader,
        selectedItem,
        editSubmitHandler: async () => await EditDietRecall({ formdata, id }),
        addSubmitHandler: async () => await AddDietRecall({ formdata, id }),
        setSelectedClient,
        key: "dietRecall",
        handleCloseModal,
        setSelectedItem,
        setFormdata,
        setErrorMessage,
        defaultHandler,
        errorDefault: {
          waterIntake: false,
        },
      });
    } else if (persona === "client") {
      const docId = uuidv4();

      const spreadData = {
        ...formdata,
      };

      setMainData((data) => {
        if (selectedItem) {
          const updatedData = updateRow(selectedItem.id, spreadData, data);
          data = updatedData;
        } else {
          data = [{ id: docId, ...spreadData, createdAt: new Date() }, ...data];
        }
        return data;
      });
      handleCloseModal();
    }
  };

  useEffect(() => {
    if (selectedItem !== null && open) {
      setFormdata({
        ...selectedItem,
        frequencyOfEating: {
          value: selectedItem.frequencyOfEating
            ? selectedItem.frequencyOfEating
            : "",
          label: selectedItem.frequencyOfEating
            ? selectedItem.frequencyOfEating
            : "",
        },
        dietType: {
          value: selectedItem.dietType ? selectedItem.dietType : "",
          label: selectedItem.dietType ? selectedItem.dietType : "",
        },
      });
    }
  }, [selectedItem, open]);

  const [errorMessage, setErrorMessage] = useState({
    dietType: false,
    waterIntake: false,
    frequencyOfEating: false,
  });

  return (
    <SuiBox>
      <Grid container spacing={2}>
        <Grid item xs={12} md={3}>
          <SuiBox lineHeight={1}>
            <SuiTypography mb={1} variant="h6">
              Follows special diet?
            </SuiTypography>
            <Createable
              options={dietTypeOptions ?? []}
              disabled={mode === "view"}
              error={errorMessage?.dietType}
              value={formdata.dietType ?? ""}
              style={{ borderRadius: "0.5rem", fontSize: 14 }}
              inputStyle={{
                width: 190,
                height: "1.800rem",
                borderRadius: "0.5rem",
              }}
              placeholder="Choose diet type"
              onChange={(e) => {
                setFormdata({
                  ...formdata,
                  dietType: e,
                });

                setErrorMessage({
                  ...errorMessage,
                  dietType: false,
                });
              }}
            />
          </SuiBox>
        </Grid>

        <Grid item xs={12} md={3}>
          <SuiBox lineHeight={1}>
            <SuiTypography mb={1} variant="h6">
              Frequency Of Eating
            </SuiTypography>
            <Createable
              disabled={mode === "view"}
              options={frequencyOfEatingOptions ?? []}
              error={errorMessage?.frequencyOfEating}
              value={formdata.frequencyOfEating ?? ""}
              style={{ borderRadius: "0.5rem", fontSize: 14 }}
              inputStyle={{
                width: 190,
                height: "1.800rem",
                borderRadius: "0.5rem",
              }}
              placeholder="Frequency Of Eating"
              onChange={(e) => {
                setFormdata({
                  ...formdata,
                  frequencyOfEating: e,
                });

                setErrorMessage({
                  ...errorMessage,
                  weekPlan: false,
                });
              }}
            />
          </SuiBox>
        </Grid>

        <Grid item xs={12} md={3}>
          <SuiBox lineHeight={1} mb={2}>
            <SuiTypography mb={1} variant="h6">
              Water Intake (L)
            </SuiTypography>
            <SuiInput
              disabled={mode === "view"}
              type="text"
              name="waterIntake"
              readOnly={mode === "view"}
              error={errorMessage?.waterIntake}
              value={formdata.waterIntake ?? ""}
              onChange={(e) => {
                let numberRegex = /^[0-9][0-9]{0,5}?(\.[0-9]{1,2})?$/;
                let val = e.target.value;
                let match_ = val.match(numberRegex);
                if (!match_ && val) {
                  return false;
                }
                setFormdata({
                  ...formdata,
                  [e.target.name]: e.target.value,
                });
                setErrorMessage({
                  ...errorMessage,
                  [e.target.name]: false,
                });
              }}
            />
          </SuiBox>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        {daySegments.map((item, index) => (
          <Grid item xs={12} lg={12} key={index}>
            <RecallChart
              title={item.title}
              chartId={item.id}
              chartError={chartError}
              mode={mode}
              setChartError={setChartError}
              setFormdata={setFormdata}
              formdata={formdata}
              open={open}
            />
          </Grid>
        ))}
        {mode !== "view" && (
          <Grid item xs={12} lg={4}>
            <CustomButton
              color="dark"
              variant="gradient"
              onClick={submitHandler}
              fullWidth
              width={180}
              disabled={loader || !open}
              title={selectedItem ? "Edit Diet Recall " : "Add Diet Recall"}
              loader={loader}
            />
          </Grid>
        )}
      </Grid>
    </SuiBox>
  );
}

export default RecallModal;
